import { Component, computed, input, output, signal } from "@angular/core";

import { CONNECTED_OVERLAY_POSITIONS_BELOW } from "@bitwarden/web-vault/app/shared/utils/overlay-positions";

/**
 * Neumorphic single-date field: a trigger showing the current date (or a placeholder)
 * that opens an `app-calendar` (single-date mode) in a CDK overlay so it escapes any
 * clipping modal. Extracted from the balance / transaction / symbol forms, which each
 * had a copy of this trigger + overlay + open/outside-click boilerplate.
 *
 * The parent owns the value: pass the formatted `displayText` in and persist on
 * `(dateChange)`.
 */
@Component({
  selector: "app-date-field",
  templateUrl: "./date-field.component.html",
})
export class DateFieldComponent {
  /** Formatted (yyyy-MM-dd) text to show in the trigger; empty/null shows the placeholder. */
  displayText = input<string | null>(null);
  /** Calendar highlight / start month, derived from displayText so callers pass one value. */
  protected selectedDate = computed(() => {
    const text = this.displayText();
    if (!text) {
      return null;
    }
    // Parse as local midnight: `new Date("yyyy-MM-dd")` is interpreted as UTC,
    // which shifts the day/month for users west of UTC.
    const [year, month, day] = text.split("-").map(Number);
    return new Date(year, month - 1, day);
  });
  /** Restricts selectable dates in the calendar. Defaults to allow-all. */
  dateFilter = input<(date: Date | null) => boolean>(() => true);
  isInvalid = input<boolean>(false);
  isDisabled = input<boolean>(false);
  placeholder = input<string>("YYYY-MM-DD");
  /** Fixed overlay width in px; when null the overlay matches the trigger width. */
  overlayWidth = input<number | null>(null);

  dateChange = output<Date>();

  protected open = signal(false);
  protected readonly positions = CONNECTED_OVERLAY_POSITIONS_BELOW;

  toggle(): void {
    if (!this.isDisabled()) {
      this.open.update((v) => !v);
    }
  }

  onPicked(date: Date): void {
    this.dateChange.emit(date);
    this.open.set(false);
  }

  onOutsideClick(event: MouseEvent, trigger: HTMLElement): void {
    if (!trigger.contains(event.target as Node)) {
      this.open.set(false);
    }
  }
}
